import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ErrorBar,
} from 'recharts';

function ucFromRow(row) {
  const terms = [
    row.ref_std_unc,
    row.repeatability_std,
    row.resolution,   // u_res (paso/√12)
    row.drift_std,
    row.other_std,
  ].filter((x) => x !== null && typeof x === 'number' && !Number.isNaN(x));
  if (!terms.length) return null;
  const sum2 = terms.reduce((acc, x) => acc + x * x, 0);
  return Math.sqrt(sum2);
}

export default function ErrorChart({ rows = [], unit = '' }) {
  const chartData = useMemo(() => {
    if (!rows || !rows.length) return [];
    return rows
      .map((r) => {
        const sp = typeof r.setpoint === 'number' ? r.setpoint : Number(r.setpoint);
        const rd = typeof r.reading === 'number' ? r.reading : Number(r.reading);
        if (!Number.isFinite(sp) || !Number.isFinite(rd)) return null;

        const y = rd - sp;                 // error = media - setpoint
        const ucRow = ucFromRow(r) || 0;   // u_c
        const krow = r.coverage_k ?? 2;    // k
        const Urow = ucRow * krow;         // U = k·u_c
        return { x: sp, y, errorY: [y - Urow, y + Urow] };
      })
      .filter(Boolean)
      .sort((a, b) => a.x - b.x);
  }, [rows]);

  const yDomain = useMemo(() => {
    if (!chartData.length) return ['auto', 'auto'];
    const lows  = chartData.map(d => d.errorY ? d.errorY[0] : d.y);
    const highs = chartData.map(d => d.errorY ? d.errorY[1] : d.y);
    const min = Math.min(...lows, 0);
    const max = Math.max(...highs, 0);
    const pad = Math.max((max - min) * 0.1, 0.001);
    return [min - pad, max + pad];
  }, [chartData]);

  if (!chartData.length) {
    return (
      <div className="text-sm text-muted-foreground border rounded p-3">
        Guardá algunas mediciones para ver el gráfico.
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: 300 }}>
      <ResponsiveContainer>
        <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="x"
            name="Setpoint"
            tickMargin={6}
            label={{ value: `Setpoint (${unit || ''})`, position: 'insideBottom', offset: -2 }}
          />
          <YAxis
            type="number"
            dataKey="y"
            name="Error"
            domain={yDomain}
            tickMargin={6}
            // Etiqueta del Eje Y: Simplificada y con mejor posición
            label={{ value: `Error ${unit ? `(${unit})` : ''}`, angle: -90, position: 'insideLeft', offset: 5, style: { textAnchor: 'middle' } }}
          />
          <Tooltip
            // Formato de Tooltip mejorado para mostrar la unidad y etiquetas claras
            formatter={(value, name) => {
              if (name === 'y') return [`${Number(value).toFixed(6)} ${unit || ''}`, 'Error (Media - Setpoint)'];
              return [`${Number(value).toFixed(6)} ${unit || ''}`, name];
            }}
            labelFormatter={(v) => `Setpoint: ${v} ${unit || ''}`}
          />
          {/* Línea de referencia y=0: Más destacada */}
          <ReferenceLine y={0} stroke="#444" strokeDasharray="3 3" strokeWidth={2} />
          {/* Scatter y ErrorBar: Estilo y color profesional unificado */}
          <Scatter 
              name="Mediciones" 
              data={chartData}
              fill="#007bff" // Color azul vibrante para los puntos
          >
            <ErrorBar 
                dataKey="errorY" 
                width={6}        // Ancho ligeramente mayor para claridad
                direction="y" 
                strokeWidth={1.5}  // Grosor de línea de error
                stroke="#007bff" // Mismo color para la barra de error
            />
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}